// Een datumveld met een kalender als popup: maandag eerst, weeknummers, vandaag omrand en de looptijd van de taak licht
// gekleurd, met de andere datum (start of deadline) gemarkeerd. Een datum in een vroegere maand opent de kalender op
// deze maand: wie een datum kiest, plant meestal vooruit. Pijltjes verplaatsen de focus een dag of een week, Page Up en
// Page Down een maand; Escape of ernaast klikken sluit. Zonder onPick is het veld alleen-lezen; een lege waarde toont
// "Kies een datum".
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Icon } from "../lib/icons";
import { addDays, ddmm, dmy, dow, isoWeek, mondayOf } from "../lib/planning";

const MONTHS = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const DAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const DAYS_LONG = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];

const ymd = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
const parts = (s: string) => s.slice(0, 10).split("-").map(Number) as [number, number, number];
const firstOf = (s: string) => s.slice(0, 8) + "01";
/** dezelfde dag n maanden verder; de 31ste wordt de laatste dag van een kortere maand */
function addMonths(s: string, n: number) {
  const [y, m, d] = parts(s);
  return ymd(y, m - 1 + n, Math.min(d, new Date(Date.UTC(y, m + n, 0)).getUTCDate()));
}
const longDay = (s: string) => { const [y, m, d] = parts(s); return DAYS_LONG[dow(s)] + " " + d + " " + MONTHS[m - 1] + " " + y; };

export function DateField({ label, value, was, today, other, otherLabel, align = "left", onPick, onReset }: {
  label: string;
  /** de gekozen datum, of "" als er nog geen is */
  value: string;
  /** de oorspronkelijke datum, als die gewijzigd is (nog niet opgeslagen) */
  was?: string | null;
  today: string;
  /** de andere datum van de taak: samen met value de looptijd */
  other?: string;
  otherLabel?: string;
  align?: "left" | "right";
  onPick?: (d: string) => void;
  onReset?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(value || today);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const toDay = useRef(false); // de focus naar de dag zetten: bij openen en bij de pijltjes, niet bij de maandknoppen

  // bij openen: de kalender in beeld en de focus op de gekozen dag; daarna volgt de focus de pijltjes
  useEffect(() => { if (open) pop.current?.scrollIntoView({ block: "nearest" }); }, [open]);
  useEffect(() => {
    if (open && toDay.current) { toDay.current = false; grid.current?.querySelector<HTMLButtonElement>('[data-d="' + focus + '"]')?.focus(); }
  }, [open, focus]);

  if (!onPick) return <div className="ex-field"><span className="ex-label">{label}</span><div className="ex-input p-fake">{value ? dmy(value) : "—"}</div></div>;

  const show = () => { toDay.current = true; setFocus(!value || value < firstOf(today) ? today : value); setOpen(true); };
  const close = () => { setOpen(false); btn.current?.focus(); };
  const pick = (d: string) => { onPick(d); close(); };

  const month = firstOf(focus), [y, m] = parts(month);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const weeks = Math.ceil((dow(month) + days) / 7);
  const from = mondayOf(month);
  const [lo, hi] = value && other ? (other < value ? [other, value] : [value, other]) : [value, value];

  const keys = (e: KeyboardEvent) => {
    const step = ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number>)[e.key];
    if (step && grid.current?.contains(e.target as Node)) { e.preventDefault(); toDay.current = true; setFocus(addDays(focus, step)); }
    else if (e.key === "PageUp" || e.key === "PageDown") { e.preventDefault(); toDay.current = true; setFocus(addMonths(focus, e.key === "PageUp" ? -1 : 1)); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
  };

  return (
    <div className="ex-field">
      <span className="ex-label">{label}</span>
      <div className="p-ddwrap p-df">
        <button ref={btn} type="button" className={"ex-input p-fake p-df-btn" + (was ? " new" : "")} aria-haspopup="dialog" aria-expanded={open}
          title={label + " kiezen"} onClick={() => (open ? close() : show())}>
          {value ? <span>{dmy(value)}</span> : <span className="t-mute">Kies een datum</span>}
          {was && <span className="p-df-was">was {ddmm(was)}</span>}
          <Icon name="cal" size={16} />
        </button>
        {open && <>
          <button type="button" className="p-dd-scrim" aria-label="Kalender sluiten" tabIndex={-1} onClick={close} />
          <div ref={pop} className={"p-pop p-calp" + (align === "right" ? " p-pop-r" : "")} role="dialog" aria-label={label + " kiezen"} onKeyDown={keys}>
            <div className="p-calp-h">
              <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Vorige maand" title="Vorige maand" onClick={() => setFocus(addMonths(focus, -1))}><Icon name="left" size={16} /></button>
              <b aria-live="polite">{MONTHS[m - 1]} {y}</b>
              <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Volgende maand" title="Volgende maand" onClick={() => setFocus(addMonths(focus, 1))}><Icon name="right" size={16} /></button>
            </div>
            <div ref={grid} className="p-calp-g">
              <span className="wk" title="Weeknummer">wk</span>
              {DAYS.map((d) => <span key={d} className="dn">{d}</span>)}
              {Array.from({ length: weeks }, (_, w) => {
                const mon = addDays(from, w * 7);
                return [
                  <span key={"w" + mon} className="wk">{isoWeek(mon)}</span>,
                  ...Array.from({ length: 7 }, (_, i) => {
                    const d = addDays(mon, i);
                    const cls = [d.slice(0, 7) !== month.slice(0, 7) && "out", i > 4 && "we", d >= lo && d <= hi && "in", d === lo && "lo", d === hi && "hi",
                      d === today && "td", d === other && "mk", d === value && "on"].filter(Boolean).join(" ");
                    const note = d === value ? " · " + label.toLowerCase() : d === other && otherLabel ? " · " + otherLabel.toLowerCase() : d === today ? " · vandaag" : "";
                    return (
                      <button key={d} type="button" data-d={d} className={cls} tabIndex={d === focus ? 0 : -1} aria-pressed={d === value}
                        aria-label={longDay(d) + note} title={d === other && otherLabel ? otherLabel + " " + dmy(d) : undefined} onClick={() => pick(d)}>
                        {parts(d)[2]}
                      </button>
                    );
                  }),
                ];
              })}
            </div>
            <div className="p-calp-f">
              <button type="button" className="ex-link" onClick={() => pick(today)}>Vandaag</button>
              {other && otherLabel && <span className="p-calp-k"><i />{otherLabel} {ddmm(other)}</span>}
              {was && onReset && <button type="button" className="ex-link" onClick={() => { onReset(); close(); }}>Terug naar {ddmm(was)}</button>}
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}
